import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useStoreMapping } from '@/hooks/useStoreMapping';
import { 
  AlertTriangle, RefreshCw, XCircle, Clock, Store, 
  FileText, Loader2, CheckCircle, RotateCcw, Eye, Trash2,
  Edit, ArrowRight, Package
} from 'lucide-react';

// Store badge colors
const STORE_COLORS = {
  'NORTESH': 'bg-blue-100 text-blue-700',
  'NORTESHM': 'bg-indigo-100 text-indigo-700',
  'MTS': 'bg-purple-100 text-purple-700',
  'MAIASH': 'bg-green-100 text-green-700',
  'MAIAJU': 'bg-teal-100 text-teal-700',
  'GAIA': 'bg-orange-100 text-orange-700',
  'GAIASH': 'bg-amber-100 text-amber-700',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: pt });
  } catch {
    return dateStr;
  }
};

// Analyze error type to suggest fixes
const analyzeError = (errorMsg) => {
  if (!errorMsg) return { type: 'unknown', suggestion: 'Erro desconhecido' };
  
  const msg = errorMsg.toLowerCase();
  
  if (msg.includes('quantidade suficiente') || msg.includes('stock') || msg.includes('insuficiente')) {
    return { 
      type: 'stock', 
      suggestion: 'Stock insuficiente no SAGE. Verifique o stock do artigo no armazém.',
      canRetry: true,
      canSkipLines: true,
    };
  }
  if (msg.includes('terceiro') || msg.includes('cliente') || msg.includes('não é válido')) {
    return { 
      type: 'client', 
      suggestion: 'Cliente/Terceiro inválido no SAGE. Verifique a configuração da loja.',
      canRetry: true,
    };
  }
  if (msg.includes('artigo') && (msg.includes('não existe') || msg.includes('não encontrado'))) {
    return { 
      type: 'sku', 
      suggestion: 'SKU não existe no SAGE. Precisa mapear para um SKU válido.',
      canMapSku: true,
      canRetry: true,
    };
  }
  if (msg.includes("'str'") || msg.includes('attribute') || msg.includes('typeerror')) {
    return { 
      type: 'bug', 
      suggestion: 'Erro de programação. Contacte o suporte.',
      canRetry: false,
    };
  }
  
  return { type: 'other', suggestion: 'Verifique o erro e tente reprocessar.', canRetry: true };
};

const SageErrors = () => {
  const { getStoreName } = useStoreMapping();
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [errors, setErrors] = useState([]);
  const [total, setTotal] = useState(0);
  
  // Detail modal
  const [selectedError, setSelectedError] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // SKU mapping modal
  const [showSkuMapping, setShowSkuMapping] = useState(false);
  const [skuToMap, setSkuToMap] = useState('');
  const [sageSku, setSageSku] = useState('');
  const [mappingSaving, setMappingSaving] = useState(false);

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/wintouch/sage-integration-errors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setErrors(response.data.errors || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch SAGE errors:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  const handleRetry = async (batchId, type) => {
    setRetrying(batchId);
    try {
      const token = localStorage.getItem('token');
      const endpoint = type === 'isl' 
        ? `${API}/wintouch/batch/${batchId}/retry-isl`
        : `${API}/wintouch/batch/${batchId}/retry-iel`;
      
      const response = await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        alert(`✅ ${type.toUpperCase()} criado com sucesso: ${response.data.sage_reference || ''}`);
        await fetchErrors();
      } else {
        alert(`❌ Erro: ${response.data.message || 'Falhou ao reprocessar'}`);
        await fetchErrors(); // Refresh to show new error
      }
    } catch (err) {
      alert(`❌ Erro: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRetrying(null);
    }
  };
  
  const handleViewDetail = async (error) => {
    setSelectedError(error);
    setLoadingDetail(true);
    setBatchDetail(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/wintouch/batch/${error.batch_id}/error-detail`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatchDetail(response.data);
    } catch (err) {
      console.error('Failed to load batch detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };
  
  const handleMapSku = (sku) => {
    setSkuToMap(sku);
    setSageSku('');
    setShowSkuMapping(true);
  };
  
  const handleSaveSkuMapping = async () => {
    if (!skuToMap || !sageSku) {
      alert('Preencha ambos os campos');
      return;
    }
    
    setMappingSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/sku-exceptions/mappings`, 
        { wintouch_sku: skuToMap.toUpperCase(), sage_sku: sageSku.toUpperCase(), description: 'Criado em Erros SAGE' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert(`✅ Mapeamento criado: ${skuToMap} → ${sageSku}`);
      setShowSkuMapping(false);
      // Refresh detail if open
      if (selectedError) {
        handleViewDetail(selectedError);
      }
    } catch (err) {
      alert(`Erro ao criar mapeamento: ${err.response?.data?.detail || err.message}`);
    } finally {
      setMappingSaving(false);
    }
  };
  
  const handleDismissError = async (batchId) => {
    const reason = window.prompt('Arquivar este erro (o batch e os documentos ficam guardados). Indique o motivo:');
    if (!reason || !reason.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/wintouch/batch/${batchId}/dismiss-error`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedError(null);
      setBatchDetail(null);
      await fetchErrors();
    } catch (err) {
      alert(`Erro ao arquivar: ${err.response?.data?.detail || err.message}`);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <CardTitle className="text-lg">Erros de Integração SAGE</CardTitle>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchErrors}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-2">Atualizar</span>
            </Button>
          </div>
          <CardDescription>
            Batches que falharam ao criar documentos ISL/IEL no SAGE. Pode ver detalhes, corrigir e reprocessar.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-red-500" />
              <span className="ml-3 text-slate-500">A carregar erros...</span>
            </div>
          ) : errors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Sem Erros de Integração</h3>
              <p className="text-slate-500 mt-1">Todos os batches foram integrados com sucesso no SAGE</p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <span className="font-medium text-red-700">
                    {total} batch{total !== 1 ? 'es' : ''} com erros de integração
                  </span>
                </div>
                <p className="text-sm text-red-600 mt-1">
                  Clique em "Detalhes" para ver as linhas do batch e corrigir problemas específicos.
                </p>
              </div>

              {/* Errors Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-[100px]">Loja</TableHead>
                      <TableHead className="w-[100px]">Data</TableHead>
                      <TableHead>Erro</TableHead>
                      <TableHead className="w-[200px]">Sugestão</TableHead>
                      <TableHead className="w-[180px]">Acções</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errors.map((error, idx) => {
                      const analysis = analyzeError(error.error_message);
                      return (
                        <TableRow key={idx} className="bg-red-50/30">
                          <TableCell>
                            <Badge className={STORE_COLORS[error.store_code] || 'bg-slate-100 text-slate-700'}>
                              {error.store_code ? getStoreName(error.store_code) : '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {error.business_date || '-'}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-red-700 max-w-[300px] truncate" title={error.error_message}>
                              {error.error_message || 'Erro desconhecido'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs text-slate-600">
                              {analysis.suggestion}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => handleViewDetail(error)}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                Detalhes
                              </Button>
                              {analysis.canRetry && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs bg-green-50 hover:bg-green-100 text-green-700"
                                  onClick={() => handleRetry(error.batch_id, error.iel_failed && !error.isl_failed ? 'iel' : 'isl')}
                                  disabled={retrying === error.batch_id}
                                >
                                  {retrying === error.batch_id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <RotateCcw className="h-3 w-3 mr-1" />
                                  )}
                                  Reprocessar
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Detail Modal */}
      <Dialog open={!!selectedError} onOpenChange={() => { setSelectedError(null); setBatchDetail(null); }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Detalhe do Batch com Erro
            </DialogTitle>
            <DialogDescription>
              {selectedError && `${getStoreName(selectedError.store_code)} - ${selectedError.business_date}`}
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : batchDetail ? (
            <div className="space-y-4">
              {/* Error Message */}
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="font-medium text-red-700">Erro:</div>
                <div className="text-sm text-red-600 mt-1">{selectedError?.error_message}</div>
              </div>
              
              {/* Batch Info */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Batch ID:</span>
                  <span className="ml-2 font-mono">{batchDetail.batch_id?.slice(0, 20)}...</span>
                </div>
                <div>
                  <span className="text-slate-500">Armazém SAGE:</span>
                  <span className="ml-2 font-medium">{batchDetail.sage_warehouse || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500">Total Linhas:</span>
                  <span className="ml-2 font-medium">{batchDetail.lines?.length || 0}</span>
                </div>
              </div>
              
              {/* Lines Table */}
              <div>
                {batchDetail.missing_in_catalog?.length > 0 && (
                  <div className="mb-3 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="sage-error-missing-skus">
                    Artigos que não existem no catálogo SAGE: <span className="font-mono">{batchDetail.missing_in_catalog.join(', ')}</span>. Use "Mapear" para indicar o artigo SAGE correto e depois "Reprocessar".
                  </div>
                )}
                <h4 className="font-medium text-slate-700 mb-2">Linhas do Batch:</h4>
                <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-[60px]">Doc</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>Artigo SAGE</TableHead>
                        <TableHead className="w-[80px]">Qtd</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="w-[150px]">Acções</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(batchDetail.lines || []).map((line, idx) => (
                        <TableRow key={idx} className={line.in_catalog ? '' : 'bg-amber-50'} data-testid={`sage-error-line-${line.sku}`}>
                          <TableCell className="text-xs text-slate-500">{line.doc_type}</TableCell>
                          <TableCell className="font-mono text-sm">{line.sku || '-'}</TableCell>
                          <TableCell className="font-mono text-sm">
                            {line.effective_sku}
                            {!line.in_catalog && <span className="ml-1 text-[10px] text-amber-700">não existe no SAGE</span>}
                          </TableCell>
                          <TableCell>{line.quantity || 0}</TableCell>
                          <TableCell className="text-sm text-slate-600 truncate max-w-[200px]">
                            {line.description || '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 text-xs text-blue-600 hover:text-blue-800"
                                onClick={() => handleMapSku(line.sku)}
                                data-testid={`sage-error-map-${line.sku}`}
                                title="Mapear este SKU para outro"
                              >
                                <ArrowRight className="h-3 w-3 mr-1" />
                                Mapear
                              </Button>

                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              
              {/* Actions */}
              <DialogFooter className="flex justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDismissError(batchDetail.batch_id)}
                  data-testid="sage-error-dismiss-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Arquivar erro
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => { setSelectedError(null); setBatchDetail(null); }}
                  >
                    Fechar
                  </Button>
                  <Button
                    onClick={() => handleRetry(batchDetail.batch_id, batchDetail.sage_isl_reference && batchDetail.iel_required && !batchDetail.sage_iel_reference ? 'iel' : 'isl')}
                    disabled={retrying === batchDetail.batch_id}
                  >
                    {retrying === batchDetail.batch_id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <RotateCcw className="h-4 w-4 mr-2" />
                    )}
                    {batchDetail.sage_isl_reference && batchDetail.iel_required && !batchDetail.sage_iel_reference ? 'Reprocessar IEL' : 'Reprocessar ISL'}
                  </Button>
                </div>
              </DialogFooter>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              Não foi possível carregar os detalhes do batch
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* SKU Mapping Modal */}
      <Dialog open={showSkuMapping} onOpenChange={setShowSkuMapping}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mapear SKU</DialogTitle>
            <DialogDescription>
              Criar mapeamento entre SKU WinTouch e SKU SAGE
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>SKU WinTouch (origem)</Label>
              <Input 
                value={skuToMap} 
                onChange={(e) => setSkuToMap(e.target.value.toUpperCase())}
                placeholder="Ex: AAA01BOB06"
              />
            </div>
            <div className="flex items-center justify-center">
              <ArrowRight className="h-5 w-5 text-slate-400" />
            </div>
            <div className="space-y-2">
              <Label>SKU SAGE (destino)</Label>
              <Input 
                value={sageSku} 
                onChange={(e) => setSageSku(e.target.value.toUpperCase())}
                placeholder="Ex: BOB06SAGE"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSkuMapping(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveSkuMapping} disabled={mappingSaving}>
              {mappingSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Guardar Mapeamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SageErrors;
